import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  Switch,
  Popconfirm,
  App,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { tagsAPI } from '../services/api'

interface Tag {
  id: number
  name: string
  slug: string
  description?: string
  featured: boolean
  isActive: boolean
}

const Tags: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [form] = Form.useForm()
  const { message } = App.useApp()

  // Load tags from API
  useEffect(() => {
    loadTags()
  }, [])

  const loadTags = async () => {
    try {
      setLoading(true)
      const response = await tagsAPI.getAll()
      setTags(response.data)
    } catch (error) {
      console.error('Error loading tags:', error)
      message.error('Failed to load tags')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingTag(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag)
    form.setFieldsValue(tag)
    setIsModalVisible(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await tagsAPI.delete(id.toString())
      message.success('Tag deleted successfully')
      loadTags()
    } catch (error) {
      console.error('Error deleting tag:', error)
      message.error('Failed to delete tag')
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      const tagData = {
        ...values,
        featured: values.featured || false,
      }
      
      if (editingTag) {
        // Update existing tag
        await tagsAPI.update(editingTag.id.toString(), tagData)
        message.success('Tag updated successfully')
      } else {
        // Create new tag
        await tagsAPI.create(tagData)
        message.success('Tag created successfully')
      }
      
      setIsModalVisible(false)
      form.resetFields()
      loadTags()
    } catch (error) {
      console.error('Error saving tag:', error)
      message.error('Failed to save tag')
    }
  }

  const handleStatusChange = async (id: number, isActive: boolean) => {
    try {
      await tagsAPI.update(id.toString(), { isActive })
      message.success('Tag status updated successfully')
      loadTags()
    } catch (error) {
      console.error('Error updating tag status:', error)
      message.error('Failed to update tag status')
    }
  }

  const handleFeaturedChange = async (id: number, featured: boolean) => {
    try {
      await tagsAPI.update(id.toString(), { featured })
      message.success('Tag featured status updated successfully')
      loadTags()
    } catch (error) {
      console.error('Error updating tag featured status:', error)
      message.error('Failed to update tag featured status')
    }
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: Tag) =>
        record.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => description || '-',
    },
    {
      title: 'Featured',
      dataIndex: 'featured',
      key: 'featured',
      render: (featured: boolean, record: Tag) => (
        <Switch
          checked={featured}
          onChange={(checked) => handleFeaturedChange(record.id, checked)}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: Tag) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Tag) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this tag?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <h2>Tags Management</h2>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Tag
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={tags}
          rowKey="id"
          loading={loading}
          pagination={{
            total: tags.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>

      <Modal
        title={editingTag ? 'Edit Tag' : 'Add Tag'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Tag Name"
            rules={[
              { required: true, message: 'Please enter tag name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug"
            rules={[
              { required: true, message: 'Please enter slug' },
              { pattern: /^[a-z0-9-]+$/, message: 'Slug can only contain lowercase letters, numbers, and hyphens' },
            ]}
          >
            <Input placeholder="Enter slug (e.g., action-adventure)" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: false }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter tag description (optional)"
            />
          </Form.Item>

          <Form.Item
            name="featured"
            label="Featured"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTag ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Tags
